import React, { useEffect, useState } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Linking,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { useRoute, useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import { MotiView } from "moti";
import {
  ArrowLeft,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  Lightbulb,
  Star,
  BookOpen,
  ClipboardText,
  FileText,
  TrendUp,
  Shield,
  Question,
  Link as LinkIcon,
  Copy,
  Check,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";

import { useGetLatestCourseEvaluationQuery, useEvaluateCourseMutation } from "../../graphql/generated/graphql";

import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomModal from "../../components/common/general/CustomModal/CustomModal";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { formatDateWithTime, formatSemester } from "../../utils/date";
import AssignmentAccordion, {
  type AssignmentAnalysis,
} from "../../components/adminDashboard/AssignmentAccordion/AssignmentAccordion";

// Badge component for effectiveness ratings
const EffectivenessBadge = ({ rating }: { rating: string }) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return { bg: "#ECFDF3", text: "#027A48", border: "#A6F4C5" };
      case "good":
        return { bg: "#EEF4FF", text: "#3538CD", border: "#C7D7FE" };
      case "fair":
        return { bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
      case "poor":
        return { bg: "#FEF3F2", text: "#B42318", border: "#FECDCA" };
      default:
        return { bg: "#F2F4F7", text: "#344054", border: "#E4E7EC" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.effectivenessBadge, { backgroundColor: style.bg, borderColor: style.border }]}>
      <CustomText useTranslationText={false} text={rating} size="xs" weight="medium" style={{ color: style.text }} />
    </View>
  );
};

// Curriculum Evaluation Badge with different styling
const CurriculumEvaluationBadge = ({ rating }: { rating: string }) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "good":
        return { bg: "rgb(239, 246, 255)", text: "rgb(29, 78, 216)", border: "rgb(191, 219, 254)" };
      default:
        return { bg: "rgb(239, 246, 255)", text: "rgb(29, 78, 216)", border: "rgb(191, 219, 254)" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.curriculumEvaluationBadge, { backgroundColor: style.bg, borderColor: style.border }]}>
      <CustomText useTranslationText={false} text={rating} size="s" weight="bold" style={{ color: style.text }} />
    </View>
  );
};

// Assignment Evaluation Badge with larger styling
const AssignmentEvaluationBadge = ({ rating }: { rating: string }) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return { bg: "#ECFDF3", text: "#027A48", border: "#A6F4C5" };
      case "good":
        return { bg: "#EEF4FF", text: "#3538CD", border: "#C7D7FE" };
      case "fair":
        return { bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
      case "poor":
        return { bg: "#FEF3F2", text: "#B42318", border: "#FECDCA" };
      default:
        return { bg: "#F2F4F7", text: "#344054", border: "#E4E7EC" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.assignmentEvaluationBadge, { backgroundColor: style.bg, borderColor: style.border }]}>
      <CustomText useTranslationText={false} text={rating} size="s" weight="bold" style={{ color: style.text }} />
    </View>
  );
};

// Helper function to parse improvement suggestion with rubric anchoring and effect
function parseImprovementSuggestion(suggestion: string): {
  rubricAnchor: string;
  suggestionText: string;
  effect: string;
  isAnchored: boolean;
} {
  // Format: "Essential Design - Objective 3: [suggestion] (Effect: [effect])"
  const anchoredPattern = /^(.+?)\s*-\s*Objective\s+(\d+):\s*(.+?)\s*\(Effect:\s*(.+)\)$/;
  const match = suggestion.match(anchoredPattern);

  if (match) {
    return {
      rubricAnchor: `${match[1]} - Objective ${match[2]}`,
      suggestionText: match[3],
      effect: match[4],
      isAnchored: true,
    };
  }

  // Fallback for non-anchored suggestions (backward compatibility)
  return {
    rubricAnchor: "",
    suggestionText: suggestion,
    effect: "",
    isAnchored: false,
  };
}

function CourseEvaluatorDetailView() {
  const route = useRoute();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { courseId } = route.params as { courseId: string };

  const { data, loading, refetch } = useGetLatestCourseEvaluationQuery({
    variables: { courseId },
    fetchPolicy: "network-only",
  });

  const evaluation = data?.getLatestCourseEvaluation;
  const [evaluateCourse, { loading: evaluating }] = useEvaluateCourseMutation();
  const isPending = evaluation?.status === "pending";
  const PENDING_STALE_MS = 5 * 60 * 1000; // 5 min - after this, allow "Try again" (backend will replace stale pending)
  const pendingCreatedAt = evaluation?.createdAt ? new Date(evaluation.createdAt).getTime() : 0;
  const isPendingStale = isPending && pendingCreatedAt > 0 && Date.now() - pendingCreatedAt > PENDING_STALE_MS;
  const [copiedSuggestionIndex, setCopiedSuggestionIndex] = useState<number | null>(null);
  const [isCourseDescriptionExpanded, setIsCourseDescriptionExpanded] = useState(false);
  const [isCourseDescriptionCopied, setIsCourseDescriptionCopied] = useState(false);
  const [copiedAssignmentIndex, setCopiedAssignmentIndex] = useState<number | null>(null);
  const [showStandardsTooltip, setShowStandardsTooltip] = useState(false);
  const [isImprovementSuggestionsExpanded, setIsImprovementSuggestionsExpanded] = useState(true);

  const handleOpenOlcScorecards = () => {
    const url = "https://onlinelearningconsortium.org/quality/scorecards/";
    Linking.openURL(url).catch(() => {});
  };

  const handleEvaluation = async () => {
    try {
      await evaluateCourse({ variables: { courseId } });
      refetch();
    } catch (err) {
      console.log("Error starting evaluation", err);
    }
  };

  useEffect(() => {
    if (!evaluation || evaluation.status !== "pending") return;
    const interval = setInterval(() => {
      refetch();
    }, 2000);
    return () => clearInterval(interval);
  }, [evaluation, refetch]);

  const curriculum = evaluation?.curriculumEvaluationJson ? JSON.parse(evaluation.curriculumEvaluationJson) : null;
  const assignmentEvaluation = evaluation?.assignmentEvaluationJson
    ? JSON.parse(evaluation.assignmentEvaluationJson)
    : null;

  const hasCompletedEvaluation = evaluation?.status === "completed" && curriculum && assignmentEvaluation;

  const formatHeaderMeta = (): string => {
    const parts: string[] = [];

    // Add course code if available
    if (evaluation?.courseHasEvaluation?.course_code) {
      parts.push(evaluation.courseHasEvaluation.course_code);
    }

    // Add semester if startDate is available
    const course = evaluation?.courseHasEvaluation as { startDate?: string | null } | null | undefined;
    const semester = formatSemester(course?.startDate);
    if (semester) {
      parts.push(semester);
    }

    // Add evaluated date with time if available
    if (evaluation?.createdAt) {
      parts.push(`Evaluated on ${formatDateWithTime(evaluation.createdAt)}`);
    }

    return parts.join(" · ");
  };

  // Get assignment stats
  const getAssignmentStats = () => {
    const analysis = assignmentEvaluation?.assignmentAnalysis || [];
    const stats = { excellent: 0, good: 0, fair: 0, poor: 0 };
    analysis.forEach((a: AssignmentAnalysis) => {
      const eff = a.effectiveness?.toLowerCase();
      if (eff === "excellent") stats.excellent++;
      else if (eff === "good") stats.good++;
      else if (eff === "fair") stats.fair++;
      else if (eff === "poor") stats.poor++;
    });
    return { total: analysis.length, ...stats };
  };

  const assignmentStats = getAssignmentStats();

  // Parse overall summary to extract scores
  const parseOverallSummary = () => {
    const summary = evaluation?.overallSummary || "";
    const lines = summary.split("\n");
    const result: {
      courseName?: string;
      totalScore?: string;
      essentialDesign?: string;
      advancedDesign?: string;
      fullText: string;
    } = { fullText: summary };

    lines.forEach((line) => {
      if (line.includes("Overall Score:")) {
        const match = line.match(/Overall Score:\s*(\d+)\/(\d+)\s*\((\d+)%\)/);
        if (match) result.totalScore = `${match[1]}/${match[2]} (${match[3]}%)`;
      } else if (line.includes("Essential Design:")) {
        const match = line.match(/Essential Design:\s*(\d+\/\d+)\s*\((\d+)%\)/);
        if (match) result.essentialDesign = `${match[1]} (${match[2]}%)`;
      } else if (line.includes("Advanced Design:")) {
        const match = line.match(/Advanced Design:\s*(\d+\/\d+)\s*\((\d+)%\)/);
        if (match) result.advancedDesign = `${match[1]} (${match[2]}%)`;
      } else if (line.includes("Course Evaluation Summary")) {
        const match = line.match(/Course Evaluation Summary\s*\((.+)\)/);
        if (match) result.courseName = match[1];
      }
    });

    return result;
  };

  const summaryData = parseOverallSummary();

  // Calculate analyzed items
  const course = evaluation?.courseHasEvaluation;
  // Note: objectives is not included in the GraphQL query, so we can't access it directly
  // We'll use 0 as a fallback since the query doesn't fetch objectives
  const objectivesCount = 0;
  const assessmentsCount = assignmentEvaluation?.assignmentAnalysis?.length || 0;

  // Parse score for progress bar
  const getScoreForProgress = () => {
    if (!summaryData.totalScore) return null;
    const match = summaryData.totalScore.match(/(\d+)\/(\d+)/);
    if (match) {
      const score = parseInt(match[1], 10);
      const max = parseInt(match[2], 10);
      const percentage = Math.round((score / max) * 100);
      return { score, max, percentage };
    }
    return null;
  };

  const scoreProgress = getScoreForProgress();

  return (
    <AdminLayout withPadding={false}>
      <ScrollView style={styles.pageBackground} tabIndex={0}>
        <View style={styles.contentContainer}>
          {/* Back button */}
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.navigate("Course Evaluator")}>
            <ArrowLeft size={16} color="#667085" />
            <CustomText
              useTranslationText={false}
              text="Back to Course Evaluator"
              size="s"
              weight="medium"
              style={styles.backButtonText}
            />
          </TouchableOpacity>

          <CustomSpacing type="vertical" size="xl" />

          {/* Header */}
          <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
            <View style={styles.headerSection}>
              <View style={styles.headerLeft}>
                <CustomText
                  useTranslationText={false}
                  text={evaluation?.courseHasEvaluation?.name || "Course Evaluation"}
                  size="l"
                  textType="display"
                  weight="ultraBold"
                  style={styles.pageTitle}
                />
                <CustomText useTranslationText={false} text={formatHeaderMeta()} size="m" style={styles.headerMeta} />
              </View>
              <View style={styles.headerActions}>
                <TouchableOpacity
                  style={styles.secondaryButton}
                  onPress={handleEvaluation}
                  disabled={evaluating || (isPending && !isPendingStale)}>
                  {evaluating || (isPending && !isPendingStale) ? (
                    <ActivityIndicator color="#344054" size="small" />
                  ) : (
                    <>
                      <ArrowsClockwise size={16} color="#344054" />
                      <CustomText
                        useTranslationText={false}
                        text={evaluation ? "Re-evaluate" : "Evaluate"}
                        size="s"
                        weight="medium"
                        style={styles.buttonText}
                      />
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </MotiView>

          <CustomSpacing type="vertical" size="xl" />

          {/* Loading state */}
          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#7F56D9" />
              <CustomSpacing type="vertical" size="s" />
              <CustomText useTranslationText={false} text="Loading evaluation..." size="m" style={styles.loadingText} />
            </View>
          )}

          {/* Pending state */}
          {isPending && (
            <View style={styles.pendingCard}>
              <ActivityIndicator size="small" color="#7F56D9" />
              <CustomText
                useTranslationText={false}
                text="Evaluation is being generated..."
                size="m"
                weight="medium"
                style={styles.pendingText}
              />
              {isPendingStale && (
                <>
                  <CustomSpacing type="vertical" size="s" />
                  <CustomText
                    useTranslationText={false}
                    text="This is taking longer than usual. The evaluation may have been interrupted. Click Re-evaluate above to try again."
                    size="s"
                    style={styles.pendingText}
                  />
                </>
              )}
            </View>
          )}

          {/* No evaluation state */}
          {!loading && !evaluation && (
            <View style={styles.emptyCard}>
              <ClipboardText size={48} color="#D0D5DD" />
              <CustomSpacing type="vertical" size="m" />
              <CustomText
                useTranslationText={false}
                text="No evaluation yet"
                size="m"
                weight="semiBold"
                style={styles.emptyTitle}
              />
              <CustomText
                useTranslationText={false}
                text="Click the Evaluate button to generate an AI-powered course evaluation"
                size="s"
                style={styles.emptyDescription}
              />
            </View>
          )}

          {/* Completed evaluation */}
          {hasCompletedEvaluation && (
            <>
              {/* Evaluation Info Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.evaluationInfoHeader}>
                    <View style={styles.evaluationInfoLeft}>
                      <View style={styles.shieldIconContainer}>
                        <Shield size={24} color="#12B76A" weight="fill" />
                      </View>
                      <View style={styles.evaluationInfoText}>
                        <CustomText
                          useTranslationText={false}
                          text="Evaluated using OLC Course Review Scorecard v1.0"
                          size="s"
                          weight="medium"
                          style={styles.evaluationInfoTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Online Learning Consortium Course Review Scorecard - Aligned with Quality Matters principles"
                          size="xs"
                          style={styles.evaluationInfoSubtitle}
                        />
                      </View>
                    </View>
                    {scoreProgress && (
                      <View style={styles.scoreDisplayContainer}>
                        <View style={styles.scoreDisplay}>
                          <CustomText
                            useTranslationText={false}
                            text={`${scoreProgress.score}/${scoreProgress.max}`}
                            size="xl"
                            weight="bold"
                            style={styles.scoreDisplayText}
                          />
                          <TouchableOpacity
                            style={styles.questionMarkButton}
                            onPress={() => setShowStandardsTooltip(true)}>
                            <Question size={16} color="#667085" weight="regular" />
                          </TouchableOpacity>
                        </View>
                        <CustomText
                          useTranslationText={false}
                          text="standards met"
                          size="xs"
                          style={styles.scoreDisplayLabel}
                        />
                      </View>
                    )}
                  </View>

                  {scoreProgress && (
                    <>
                      <View style={styles.progressBarContainer}>
                        <View style={styles.progressBarBackground}>
                          <View style={[styles.progressBarFill, { width: `${scoreProgress.percentage}%` }]} />
                        </View>
                      </View>
                      <View style={styles.progressBarFooter}>
                        <CustomText
                          useTranslationText={false}
                          text={`${scoreProgress.percentage}% of standards met`}
                          size="xs"
                          style={styles.progressBarText}
                        />
                      </View>
                    </>
                  )}

                  {/* What Was Analyzed */}
                  <View style={styles.analyzedSection}>
                    <CustomText
                      useTranslationText={false}
                      text="What Was Analyzed"
                      size="s"
                      weight="bold"
                      style={styles.analyzedSectionTitle}
                    />
                    <View style={styles.analyzedItemsGrid}>
                      {assessmentsCount > 0 && (
                        <View style={styles.analyzedItem}>
                          <CheckCircle size={18} color="#12B76A" weight="fill" />
                          <CustomText
                            useTranslationText={false}
                            text={`${assessmentsCount} assessment${assessmentsCount !== 1 ? "s" : ""}`}
                            size="xs"
                            style={styles.analyzedItemText}
                          />
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              </MotiView>

              <CustomSpacing type="vertical" size="l" />

              {/* Curriculum Evaluation Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeaderWithBadge}>
                    <View style={styles.cardHeaderLeft}>
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          backgroundColor: "rgb(250, 245, 255)",
                          justifyContent: "center",
                          alignItems: "center",
                        }}>
                        <BookOpen size={24} color="#7F56D9" />
                      </View>
                      <View>
                        <CustomText
                          useTranslationText={false}
                          text="Curriculum Evaluation"
                          size="xl"
                          weight="bold"
                          style={styles.cardTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Analysis of curriculum completeness and essential concept coverage"
                          size="s"
                          style={styles.cardSubtitle}
                        />
                      </View>
                    </View>
                  </View>

                  {/* Course Summary */}
                  {curriculum.conceptCoverage && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <FileText size={16} color="#667085" weight="regular" />
                        <CustomText
                          useTranslationText={false}
                          text="Course Evaluation Summary"
                          size="s"
                          weight="bold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      <View style={styles.conceptCoverageContainer}>
                        <CustomText
                          useTranslationText={false}
                          text={curriculum.conceptCoverage}
                          size="s"
                          weight="normal"
                          style={styles.subsectionText}
                        />
                      </View>
                      <CustomSpacing type="vertical" size="l" />
                    </View>
                  )}

                  {/* Suggested Course Description */}
                  {curriculum.suggestedCourseDescription && (
                    <View style={styles.subsection}>
                      <TouchableOpacity
                        style={styles.collapsibleCardHeader}
                        onPress={() => setIsCourseDescriptionExpanded(!isCourseDescriptionExpanded)}
                        activeOpacity={0.7}>
                        <View style={styles.collapsibleCardHeaderLeft}>
                          <FileText size={16} color="#667085" weight="regular" />
                          <CustomText
                            useTranslationText={false}
                            text="Suggested Course Description"
                            size="s"
                            weight="bold"
                            style={styles.subsectionTitle}
                          />
                        </View>
                        <View style={styles.collapsibleCardHeaderRight}>
                          <TouchableOpacity
                            style={styles.headerCopyButton}
                            onPress={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(curriculum.suggestedCourseDescription);
                              setIsCourseDescriptionCopied(true);
                              setTimeout(() => setIsCourseDescriptionCopied(false), 2000);
                            }}>
                            {isCourseDescriptionCopied ? (
                              <Check size={16} color="#12B76A" weight="bold" />
                            ) : (
                              <Copy size={16} color="#667085" weight="regular" />
                            )}
                          </TouchableOpacity>
                          {isCourseDescriptionExpanded ? (
                            <CaretUp size={16} color="#667085" weight="regular" />
                          ) : (
                            <CaretDown size={16} color="#667085" weight="regular" />
                          )}
                        </View>
                      </TouchableOpacity>
                      {isCourseDescriptionExpanded && (
                        <View style={styles.collapsibleCardContent}>
                          <View style={styles.conceptCoverageContainer}>
                            <CustomText
                              useTranslationText={false}
                              text={curriculum.suggestedCourseDescription}
                              size="s"
                              weight="normal"
                              style={styles.subsectionText}
                            />
                          </View>
                        </View>
                      )}
                      <CustomSpacing type="vertical" size="l" />
                    </View>
                  )}

                  {/* Strengths */}
                  {curriculum.strengths && curriculum.strengths.length > 0 && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <Star size={16} color="#12B76A" weight="regular" />
                        <CustomText
                          useTranslationText={false}
                          text="Strengths"
                          size="s"
                          weight="bold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      <View style={styles.strengthsGrid}>
                        {curriculum.strengths.map((item: string, idx: number) => (
                          <View key={idx} style={styles.strengthItem}>
                            <CheckCircle size={20} color="rgb(22, 163, 74)" weight="regular" />
                            <CustomText useTranslationText={false} text={item} size="s" style={styles.strengthText} />
                          </View>
                        ))}
                      </View>
                      <CustomSpacing type="vertical" size="l" />
                    </View>
                  )}

                  {/* Improvement Suggestions */}
                  {curriculum.suggestions && curriculum.suggestions.length > 0 && (
                    <View style={styles.subsection}>
                      <TouchableOpacity
                        style={styles.collapsibleCardHeader}
                        onPress={() => setIsImprovementSuggestionsExpanded(!isImprovementSuggestionsExpanded)}
                        activeOpacity={0.7}>
                        <View style={styles.collapsibleCardHeaderLeft}>
                          <Lightbulb size={16} color="#3b83f6" weight="regular" />
                          <CustomText
                            useTranslationText={false}
                            text="Improvement Suggestions"
                            size="s"
                            weight="bold"
                            style={styles.subsectionTitle}
                          />
                          {!isImprovementSuggestionsExpanded && (
                            <CustomText
                              useTranslationText={false}
                              text={`(${curriculum.suggestions.length})`}
                              size="s"
                              weight="medium"
                              style={styles.suggestionCountText}
                            />
                          )}
                        </View>
                        {isImprovementSuggestionsExpanded ? (
                          <CaretUp size={16} color="#667085" weight="regular" />
                        ) : (
                          <CaretDown size={16} color="#667085" weight="regular" />
                        )}
                      </TouchableOpacity>
                      {isImprovementSuggestionsExpanded && (
                        <View style={styles.collapsibleCardContent}>
                          {curriculum.suggestions.map((item: string, idx: number) => {
                            const parsed = parseImprovementSuggestion(item);
                            return (
                              <View
                                key={idx}
                                style={[
                                  styles.suggestionListItem,
                                  idx === curriculum.suggestions.length - 1 && styles.suggestionListItemLast,
                                ]}>
                                <TrendUp size={20} color="rgb(30, 64, 175)" weight="regular" />
                                <View style={styles.suggestionContent}>
                                  {parsed.isAnchored && parsed.rubricAnchor && (
                                    <View style={styles.rubricAnchorContainer}>
                                      <CustomText
                                        useTranslationText={false}
                                        text={parsed.rubricAnchor}
                                        size="xs"
                                        weight="semibold"
                                        style={styles.rubricAnchorText}
                                      />
                                    </View>
                                  )}
                                  <CustomText
                                    useTranslationText={false}
                                    text={parsed.suggestionText}
                                    size="s"
                                    style={styles.suggestionItemText}
                                  />
                                  {parsed.isAnchored && parsed.effect && (
                                    <View style={styles.effectContainer}>
                                      <CustomText
                                        useTranslationText={false}
                                        text={`Effect: ${parsed.effect}`}
                                        size="xs"
                                        style={styles.effectText}
                                      />
                                    </View>
                                  )}
                                </View>
                                <TouchableOpacity
                                  style={styles.copyButton}
                                  onPress={() => {
                                    navigator.clipboard.writeText(item);
                                    setCopiedSuggestionIndex(idx);
                                    setTimeout(() => setCopiedSuggestionIndex(null), 2000);
                                  }}>
                                  {copiedSuggestionIndex === idx ? (
                                    <Check size={16} color="#12B76A" weight="bold" />
                                  ) : (
                                    <Copy size={16} color="#667085" weight="regular" />
                                  )}
                                </TouchableOpacity>
                              </View>
                            );
                          })}
                        </View>
                      )}
                    </View>
                  )}
                </View>
              </MotiView>

              {/* Assignment Evaluation Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeaderWithBadge}>
                    <View style={styles.cardHeaderLeft}>
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          backgroundColor: "rgb(255, 247, 237)",
                          justifyContent: "center",
                          alignItems: "center",
                        }}>
                        <ClipboardText size={24} color="rgb(234, 88, 12)" />
                      </View>
                      <View>
                        <CustomText
                          useTranslationText={false}
                          text="Assignment Evaluation"
                          size="xl"
                          weight="bold"
                          style={styles.cardTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Evaluation of how well assignments gauge understanding and cover concepts"
                          size="s"
                          style={styles.cardSubtitle}
                        />
                      </View>
                    </View>
                    <AssignmentEvaluationBadge rating={assignmentEvaluation.effectiveness || "N/A"} />
                  </View>

                  {/* Assignment stats */}
                  <View style={styles.assignmentStatsContainer}>
                    <View style={styles.assignmentStatsRow}>
                      <CustomText
                        useTranslationText={false}
                        text={`${assignmentStats.total} assignments evaluated`}
                        size="s"
                        weight="medium"
                        style={styles.assignmentStatsLabel}
                      />
                      <View style={styles.statsBreakdown}>
                        {assignmentStats.good > 0 && (
                          <View style={[styles.statsBadge, { backgroundColor: "#EEF4FF" }]}>
                            <CustomText
                              useTranslationText={false}
                              text={`${assignmentStats.good} Good`}
                              size="xs"
                              weight="medium"
                              style={{ color: "#3538CD" }}
                            />
                          </View>
                        )}
                        {assignmentStats.fair > 0 && (
                          <View style={[styles.statsBadge, { backgroundColor: "#FFFAEB" }]}>
                            <CustomText
                              useTranslationText={false}
                              text={`${assignmentStats.fair} Fair`}
                              size="xs"
                              weight="medium"
                              style={{ color: "#B54708" }}
                            />
                          </View>
                        )}
                        {assignmentStats.excellent > 0 && (
                          <View style={[styles.statsBadge, { backgroundColor: "#ECFDF3" }]}>
                            <CustomText
                              useTranslationText={false}
                              text={`${assignmentStats.excellent} Excellent`}
                              size="xs"
                              weight="medium"
                              style={{ color: "#027A48" }}
                            />
                          </View>
                        )}
                      </View>
                    </View>

                    {assignmentEvaluation.conceptCoverageByAssignments && (
                      <CustomText
                        useTranslationText={false}
                        text={assignmentEvaluation.conceptCoverageByAssignments}
                        size="s"
                        style={styles.assignmentCoverageText}
                      />
                    )}
                  </View>

                  {/* Assignment strengths */}
                  {assignmentEvaluation.strengths && assignmentEvaluation.strengths.length > 0 && (
                    <View style={styles.assignmentStrengthsContainer}>
                      <View style={styles.assignmentStrengthsGrid}>
                        {assignmentEvaluation.strengths.map((item: string, idx: number) => (
                          <View key={idx} style={styles.assignmentStrengthItem}>
                            <CheckCircle size={20} color="rgb(22, 163, 74)" weight="regular" />
                            <CustomText useTranslationText={false} text={item} size="s" style={styles.strengthText} />
                          </View>
                        ))}
                      </View>
                    </View>
                  )}

                  {/* Assignment list */}
                  {assignmentEvaluation.assignmentAnalysis && assignmentEvaluation.assignmentAnalysis.length > 0 && (
                    <View style={styles.assignmentList}>
                      {assignmentEvaluation.assignmentAnalysis.map((analysis: AssignmentAnalysis) => (
                        <AssignmentAccordion
                          key={analysis.assignmentId || analysis.title}
                          analysis={{ ...analysis, courseCode: evaluation?.courseHasEvaluation?.course_code }}
                        />
                      ))}
                    </View>
                  )}
                </View>
              </MotiView>

              {/* Suggested Assignments Card */}
              {assignmentEvaluation.missingAssignments && assignmentEvaluation.missingAssignments.length > 0 && (
                <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          backgroundColor: "rgb(239, 246, 255)",
                          justifyContent: "center",
                          alignItems: "center",
                        }}>
                        <FileText size={24} color="#3b83f6" />
                      </View>
                      <View>
                        <CustomText
                          useTranslationText={false}
                          text="Suggested Assignments"
                          size="xl"
                          weight="bold"
                          style={styles.cardTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Assignments recommended to improve course coverage"
                          size="s"
                          style={styles.cardSubtitle}
                        />
                      </View>
                    </View>

                    <View style={styles.suggestedAssignmentsList}>
                      {assignmentEvaluation.missingAssignments.map(
                        (assignment: string | { title: string; description: string }, idx: number) => {
                          // Handle both old format (string) and new format (object)
                          const isString = typeof assignment === "string";
                          const title = isString ? "Suggested Assignment" : assignment.title || "Untitled Assignment";
                          const description = isString ? assignment : assignment.description || "";
                          const displayText = isString ? assignment : `${title}\n${description}`;

                          return (
                            <View key={idx} style={styles.suggestedAssignmentItem}>
                              <FileText size={18} color="#3b83f6" weight="regular" />
                              <View style={styles.suggestedAssignmentContent}>
                                <CustomText
                                  useTranslationText={false}
                                  text={title}
                                  size="s"
                                  weight="medium"
                                  style={styles.suggestedAssignmentTitle}
                                />
                                <CustomText
                                  useTranslationText={false}
                                  text={description}
                                  size="xs"
                                  style={styles.suggestedAssignmentDescription}
                                />
                              </View>
                              <TouchableOpacity
                                style={styles.copyButton}
                                onPress={() => {
                                  navigator.clipboard.writeText(displayText);
                                  setCopiedAssignmentIndex(idx);
                                  setTimeout(() => setCopiedAssignmentIndex(null), 2000);
                                }}>
                                {copiedAssignmentIndex === idx ? (
                                  <Check size={16} color="#12B76A" weight="bold" />
                                ) : (
                                  <Copy size={16} color="#667085" weight="regular" />
                                )}
                              </TouchableOpacity>
                            </View>
                          );
                        }
                      )}
                    </View>
                  </View>
                </MotiView>
              )}
            </>
          )}
        </View>
      </ScrollView>

      {/* Standards Tooltip Modal */}
      <CustomModal
        visible={showStandardsTooltip}
        onRequestClose={() => setShowStandardsTooltip(false)}
        showCloseButton={true}>
        <View style={styles.tooltipModalContent}>
          <CustomText
            useTranslationText={false}
            text="Evaluation Standards Met"
            size="xl"
            weight="bold"
            style={styles.tooltipModalTitle}
          />
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            useTranslationText={false}
            text="This score represents how well your course meets the OLC (Online Learning Consortium) Course Review Scorecard standards."
            size="m"
            style={styles.tooltipModalText}
          />
          <CustomSpacing type="vertical" size="s" />
          <TouchableOpacity style={styles.tooltipLink} onPress={handleOpenOlcScorecards}>
            <CustomText
              useTranslationText={false}
              text="Learn more about the OLC Scorecards"
              size="m"
              weight="medium"
              style={styles.tooltipLinkText}
            />
          </TouchableOpacity>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            useTranslationText={false}
            text="Scoring System:"
            size="m"
            weight="bold"
            style={styles.tooltipModalSectionTitle}
          />
          <CustomSpacing type="vertical" size="s" />
          <View style={styles.tooltipModalList}>
            <CustomText
              useTranslationText={false}
              text="• Total Possible Score: 70 points"
              size="m"
              style={styles.tooltipModalText}
            />
            <CustomText
              useTranslationText={false}
              text="  - Essential Design: 40 points (20 objectives)"
              size="m"
              style={styles.tooltipModalText}
            />
            <CustomText
              useTranslationText={false}
              text="  - Advanced Design: 30 points (15 objectives)"
              size="m"
              style={styles.tooltipModalText}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            useTranslationText={false}
            text="Each objective is scored 0-2:"
            size="m"
            weight="bold"
            style={styles.tooltipModalSectionTitle}
          />
          <CustomSpacing type="vertical" size="s" />
          <View style={styles.tooltipModalList}>
            <CustomText
              useTranslationText={false}
              text="• 0 = Developing (does not meet the standard)"
              size="m"
              style={styles.tooltipModalText}
            />
            <CustomText
              useTranslationText={false}
              text="• 1 = Accomplished (adequately meets the standard)"
              size="m"
              style={styles.tooltipModalText}
            />
            <CustomText
              useTranslationText={false}
              text="• 2 = Exemplary (thoroughly meets the standard)"
              size="m"
              style={styles.tooltipModalText}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            useTranslationText={false}
            text="The percentage shown indicates what portion of the total standards your course has met."
            size="m"
            style={styles.tooltipModalText}
          />
        </View>
      </CustomModal>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  contentContainer: {
    padding: 32,
    maxWidth: 1024,
    width: "100%",
    alignSelf: "center",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  backButtonText: {
    color: "#475467",
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    color: "#101828",
    fontSize: 24,
    lineHeight: 32,
  },
  headerMeta: {
    color: "#475467",
    marginTop: 4,
  },
  headerActions: {
    flexDirection: "row",
    gap: 12,
  },
  secondaryButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
  },
  buttonText: {
    color: "#344054",
  },
  loadingContainer: {
    padding: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    color: "#667085",
  },
  pendingCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 20,
    backgroundColor: "#F9F5FF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E9D7FE",
  },
  pendingText: {
    color: "#7F56D9",
  },
  emptyCard: {
    padding: 60,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderStyle: "dashed",
  },
  emptyTitle: {
    color: "#344054",
  },
  emptyDescription: {
    color: "#667085",
    textAlign: "center",
    marginTop: 4,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    padding: 24,
    marginBottom: 20,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  cardHeaderWithBadge: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  cardTitle: {
    color: "#101828",
  },
  cardSubtitle: {
    color: "#475467",
    marginTop: 2,
  },
  effectivenessBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },
  statsBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  curriculumEvaluationBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 9999,
    borderWidth: 1,
  },
  assignmentEvaluationBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 9999,
    borderWidth: 1,
  },
  subsection: {
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderRadius: 8,
    padding: 16,
  },
  subsectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  subsectionTitle: {
    color: "#344054",
  },
  subsectionText: {
    color: "#475467",
    lineHeight: 22,
    ...(Platform.OS === "web"
      ? ({
          whiteSpace: "pre-line",
        } as TextStyle)
      : {}),
  },
  conceptCoverageContainer: {
    backgroundColor: "rgb(249, 250, 251)",
    padding: 16,
    borderRadius: 8,
  },
  strengthsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  strengthItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "rgb(240, 253, 244)",
    borderColor: "rgb(187, 247, 208)",
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
    flex: 1,
    minWidth: "48%",
    ...(Platform.OS === "web"
      ? ({
          maxWidth: "calc(50% - 2px)",
        } as Record<string, unknown> as ViewStyle)
      : {}),
  },
  strengthText: {
    color: "rgb(22, 101, 52)",
    flex: 1,
    lineHeight: 20,
  },
  suggestionListItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 10,
    backgroundColor: "rgb(239, 246, 255)",
    borderColor: "rgb(191, 219, 254)",
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
  },
  suggestionListItemLast: {
    marginBottom: 0,
  },
  suggestionContent: {
    flex: 1,
    gap: 6,
  },
  suggestionItemText: {
    color: "rgb(30, 64, 175)",
    lineHeight: 20,
  },
  rubricAnchorContainer: {
    marginBottom: 4,
  },
  rubricAnchorText: {
    color: "rgb(99, 102, 241)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  effectContainer: {
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: "rgba(30, 64, 175, 0.1)",
  },
  effectText: {
    color: "rgb(100, 116, 139)",
    fontStyle: "italic",
    lineHeight: 18,
  },
  copyButton: {
    padding: 4,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginLeft: 8,
  },
  collapsibleCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
  },
  collapsibleCardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  collapsibleCardHeaderRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerCopyButton: {
    padding: 4,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  collapsibleCardContent: {
    marginTop: 12,
  },
  suggestionCountText: {
    color: "#667085",
    marginLeft: 8,
  },
  assignmentStatsContainer: {
    backgroundColor: "rgb(249, 250, 251)",
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  assignmentStatsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  assignmentStatsLabel: {
    color: "#344054",
  },
  statsBreakdown: {
    flexDirection: "row",
    gap: 16,
  },
  assignmentCoverageText: {
    color: "#475467",
    lineHeight: 22,
  },
  assignmentList: {
    gap: 12,
  },
  assignmentStrengthsContainer: {
    marginBottom: 20,
  },
  assignmentStrengthsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  assignmentStrengthItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "rgb(240, 253, 244)",
    borderColor: "rgb(187, 247, 208)",
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
  },
  suggestedAssignmentsList: {
    gap: 12,
  },
  suggestedAssignmentItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    backgroundColor: "rgb(239, 246, 255)",
    borderColor: "rgb(191, 219, 254)",
    borderWidth: 1,
    borderRadius: 8,
    padding: 14,
  },
  suggestedAssignmentContent: {
    flex: 1,
    gap: 4,
  },
  suggestedAssignmentTitle: {
    color: "#1E40AF",
  },
  suggestedAssignmentDescription: {
    color: "#475467",
    lineHeight: 18,
  },
  evaluationInfoHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  evaluationInfoLeft: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    flex: 1,
  },
  shieldIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#ECFDF3",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  evaluationInfoText: {
    flex: 1,
  },
  evaluationInfoTitle: {
    color: "#101828",
    marginBottom: 4,
  },
  evaluationInfoSubtitle: {
    color: "#667085",
    lineHeight: 18,
  },
  scoreDisplayContainer: {
    alignItems: "flex-end",
    marginLeft: 16,
  },
  scoreDisplay: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  scoreDisplayText: {
    color: "#101828",
  },
  questionMarkButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#F2F4F7",
    justifyContent: "center",
    alignItems: "center",
  },
  scoreDisplayLabel: {
    color: "#667085",
  },
  progressBarContainer: {
    marginBottom: 8,
  },
  progressBarBackground: {
    height: 8,
    backgroundColor: "#E4E7EC",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    backgroundColor: "#12B76A",
    borderRadius: 4,
  },
  progressBarFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  progressBarText: {
    color: "#667085",
  },
  rubricLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  rubricLinkText: {
    color: "#3b83f6",
  },
  analyzedSection: {
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: "#E4E7EC",
  },
  analyzedSectionTitle: {
    color: "#344054",
    marginBottom: 12,
  },
  analyzedItemsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  analyzedItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  analyzedItemText: {
    color: "#475467",
  },
  tooltipModalContent: {
    padding: 8,
  },
  tooltipModalTitle: {
    color: "#101828",
    marginBottom: 8,
  },
  tooltipModalText: {
    color: "#475467",
    lineHeight: 24,
  },
  tooltipModalSectionTitle: {
    color: "#101828",
    marginTop: 8,
  },
  tooltipModalList: {
    gap: 8,
  },
  tooltipLink: {
    marginTop: 4,
  },
  tooltipLinkText: {
    color: "#3b83f6",
    textDecorationLine: "underline",
  },
});

export default CourseEvaluatorDetailView;
